// 3rd party libraries
import React, { Dispatch, ReactElement, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// State management
import { cartActions } from 'actions';

// Constants
import { sfEnabled } from 'constants/featureFlags';
import breakpoint from 'constants/breakpoints';

// Components
import { Checkbox, CustomerPrice, Typography, InformationTooltip, Input } from 'components';
import {
  CartItemRow,
  RemoveFromCartIcon,
  CartItemText,
  CartItemSku,
  QuantityContainer,
  PriceText,
  SelectControl,
  CartItemStock,
  CheckboxContainer,
  ProCareAllowance,
  CartStock,
  AvgLeadTime,
  PriceItem,
  UrgentText,
  CartUrgentText
} from '.';

// Types
import { User } from 'types';
import {
  CartListViewType,
  CartPurchasable,
  CartListType,
  CartState,
  CartListSelectionType
} from 'types/parts/cart';

// Providers
import { useLanguage } from 'providers';

// Helpers
import {
  currency,
  getPartialLeadTimeText,
  getProductDescription,
  getStockText
} from 'common/helpers/v1';

import { useCart, useUser } from 'selectors';

// hooks
import useWindowDimensions from 'hooks/useWindowDimensions';

// Api
import { useUpdateCartProductMutation } from 'api';
import { IcoTrash } from 'icons/indexed-icons/IcoTrash';
import styled from 'styled-components';
import { ToastMsg } from 'common/components/Toast';
import { TentativeDate } from 'components/CartWizard/CartWizard.elements';
import { format } from 'date-fns';
import { CartPriceType } from 'components/CartWizard/helper';

type CartRowListArguments = {
  handleClose?: () => void;
  viewType: CartListType;
  selectionType: CartListSelectionType;
  purchasable: CartPurchasable;
  selectionHandler?: (item: CartPurchasable) => void;
  removeHandler?: Dispatch<SetStateAction<CartPurchasable | undefined>>;
  qtyChangeHandler?: (purchasable: CartPurchasable, cartUpdateResult: boolean) => void;
  urgentChangeHandler?: (purchasable: CartPurchasable) => void;
  handleProCareAllowance?: (purchasable: CartPurchasable) => void;
  selectedPriceType?: CartPriceType;
  isFinalEstimationPage?: boolean;
  requestedDate?: string;
  enableProsealTwoWaySync?: boolean;
};

const Container = styled.div`
  .grid-container {
    @media (max-width: ${breakpoint.size.md}px) {
      display: grid;
      grid-template-columns: 0.5fr 2fr;
      gap: 1rem;
      width: 100%;
    }
  }

  .cartItemRow {
    @media (max-width: ${breakpoint.size.md}px) {
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
      padding: 1rem 0;
    }
  }

  .flex-align {
    display: flex;
    align-items: center;
  }

  .left {
    display: none;
  }

  @media (max-width: ${breakpoint.size.md}px) {
    .left {
      display: inline-block;
      vertical-align: top;
      margin: 10px 0 0 0;
      padding: 0;
    }

    .right {
      display: inline-block;
      vertical-align: top;
      margin: 0;
      padding: 0;
    }
  }
`;

const CartListRowComponent = ({
  viewType,
  handleClose,
  purchasable,
  selectionType,
  selectionHandler,
  removeHandler,
  qtyChangeHandler,
  handleProCareAllowance,
  urgentChangeHandler,
  selectedPriceType,
  requestedDate,
  isFinalEstimationPage,
  enableProsealTwoWaySync
}: CartRowListArguments): ReactElement => {
  const formattedDate = requestedDate ? format(new Date(requestedDate), 'dd, MMMM, yyyy') : '';

  const dispatch = useDispatch();
  const { t } = useTranslation(['fpns']);
  const [cartItem, setCartItem] = useState<CartPurchasable>(purchasable);
  const { width: screenWidth } = useWindowDimensions();

  useMemo(() => {
    setCartItem(purchasable);
  }, [purchasable]);
  const user = useUser() as User;
  const cart = useCart() as CartState;
  const { languageId } = useLanguage();
  const [updateCartProduct, updateCartResult] = useUpdateCartProductMutation();
  const changeHandler = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const rawQuantity = evt.target.value.length < 1 ? String(0) : evt.target.value;
    const newQuantity =
      Math.max(parseInt(rawQuantity), 0) == 0
        ? cartItem.quantity
        : Math.max(parseInt(rawQuantity), 0);
    const copyCartItem = { ...cartItem, ['quantity']: newQuantity };
    setCartItem(copyCartItem);
    qtyChangeHandler && qtyChangeHandler(copyCartItem, updateCartResult.isLoading);
    if (viewType === CartListType.VIEW_TYPE_DEFAULT) {
      const sfCartItem = cart.sfCart.cartItems?.find(
        (sfItem) => sfItem.cartItem.productId === copyCartItem.item.sfProductId
      );
      try {
        if (sfEnabled()) {
          updateCartProduct({
            accountId: user.organizations[0],
            cart: cart,
            product: copyCartItem.item,
            productId: copyCartItem.item.sfProductId as string,
            quantity: newQuantity,
            machineId: copyCartItem.groupId as string,
            cartItemId: sfCartItem?.cartItem.cartItemId
          });
        } else {
          dispatch({
            type: cartActions.SET_ITEM_QUANTITY,
            item: copyCartItem.item,
            groupId: copyCartItem.groupId as string,
            quantity: newQuantity
          });
        }
      } catch (error) {
        console.warn('Update to cart failed! Error:', error);
        ToastMsg({
          heading: 'Warning',
          message: 'There was a problem updating the quantity',
          type: 'warning'
        });
      }
    }
  };

  return (
    <>
      {viewType === CartListType.VIEW_TYPE_DEFAULT && screenWidth && screenWidth <= 768 ? (
        <Container>
          <CartItemRow viewType={viewType} className="cartItemRow" key={cartItem.item.id}>
            {viewType === CartListType.VIEW_TYPE_DEFAULT ? (
              <></>
            ) : viewType === CartListType.VIEW_TYPE_MORE &&
              selectionType === CartListType.SELECTION_MULTI_SELECT ? (
              <div className="grid-container">
                <p className="left">Is Urgent</p>
                <SelectControl className="right">
                  <Checkbox
                    key={`checkbox-${cartItem.item.sku}`}
                    checked={cartItem.selected as boolean}
                    onChange={() => {
                      selectionHandler && selectionHandler(cartItem);
                    }}
                  />
                </SelectControl>
              </div>
            ) : (
              viewType === CartListType.VIEW_TYPE_MORE &&
              selectionType === CartListType.SELECTION_REMOVE && <></>
            )}
            <div className="grid-container">
              <p className="left">
                <CartItemText defaultValue={viewType}>
                  <Typography weight="semi-bold">{t('parts_table_part')}</Typography>
                </CartItemText>
              </p>
              <CartItemText defaultValue={viewType} className="right">
                {/* where do we get this text from? */}

                <Typography variant="body2">
                  {getProductDescription(cartItem.item, languageId)}
                </Typography>
                {/* {viewType === CartListType.VIEW_TYPE_DEFAULT && (
                  <Typography variant="body1">{t('manufacturer_not_available')}</Typography>
                )} */}
              </CartItemText>
            </div>
            <div className="grid-container">
              <p className="left">
                {' '}
                {(viewType === CartListType.VIEW_TYPE_DEFAULT ||
                  viewType === CartListType.VIEW_TYPE_MORE) && (
                  <Typography weight="semi-bold">{t('parts_table_sku')}</Typography>
                )}
              </p>
              <div className="flex-align">
                {(viewType === CartListType.VIEW_TYPE_DEFAULT ||
                  viewType === CartListType.VIEW_TYPE_MORE) && (
                  <CartItemSku>
                    <Typography variant="body1">
                      {cartItem.item.stockCode || cartItem.item.sku}
                    </Typography>
                  </CartItemSku>
                )}
                {viewType === CartListType.VIEW_TYPE_DEFAULT && (
                  <CartItemStock>
                    <Typography variant="body1">{t(getStockText(purchasable.item))}</Typography>
                  </CartItemStock>
                )}
                <UrgentText>
                  <Typography weight="semi-bold" className="urgent">
                    {t('parts_table_urgent')}
                    <InformationTooltip
                      placement="top"
                      tooltipText={t('parts_urgent_message') as string}
                    />
                  </Typography>
                  {selectedPriceType ? (
                    'yes'
                  ) : (
                    <Checkbox
                      key={`checkbox-${cartItem.item.sku}`}
                      checked={cartItem.urgent as boolean}
                      onChange={() => {
                        urgentChangeHandler && urgentChangeHandler(cartItem);
                      }}
                      width={18}
                      height={18}
                    />
                  )}
                </UrgentText>
              </div>
            </div>
            <div className="grid-container">
              <p className="left">
                <QuantityContainer defaultValue={viewType}>
                  <Typography weight="semi-bold">{t('parts_table_quantity')}</Typography>
                </QuantityContainer>
              </p>
              <div className="flex-align">
                <QuantityContainer defaultValue={viewType}>
                  <Input
                    type="number"
                    id="qty"
                    min={1}
                    value={cartItem.quantity}
                    onChange={changeHandler}
                  />
                </QuantityContainer>
                <div>
                  {viewType === CartListType.VIEW_TYPE_DEFAULT && handleProCareAllowance && (
                    <>
                      {/* <ProCareAllowance>
                        </ProCareAllowance> */}
                      <ProCareAllowance disabled={!cartItem.item.procareAllowance}>
                        <Typography className="p-0" weight="semi-bold">
                          {'ProCare Allowance'}
                        </Typography>
                        <Checkbox
                          key={`checkbox-procare-${cartItem.item.sku}`}
                          checked={cartItem.procareAllowance as boolean}
                          onChange={() => {
                            handleProCareAllowance && handleProCareAllowance(cartItem);
                          }}
                          width={18}
                          height={18}
                          disabled={!cartItem.item.procareAllowance}
                        />
                        Yes
                      </ProCareAllowance>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="grid-container">
              <p className="left">
                <PriceText defaultValue={viewType} style={{ padding: 0 }}>
                  <Typography weight="semi-bold">{t('parts_table_price')}</Typography>
                </PriceText>
              </p>
              <PriceText defaultValue={viewType} className="right">
                <PriceItem>
                  <div className="price">
                    <Typography>
                      <CustomerPrice
                        product={cartItem.item}
                        fontSizeInREM={0.95}
                        inline={true}
                        handleClose={handleClose}
                      />
                    </Typography>

                    <Typography size={'1rem'} weight="semi-bold">
                      {typeof cartItem.item.price !== 'undefined' &&
                        cartItem.item.price > 0 &&
                        cartItem.item.isPurchasable &&
                        String(
                          currency(
                            cartItem.item.custPrice
                              ? parseFloat(cartItem.item.custPrice.toFixed(2)) * cartItem.quantity
                              : parseFloat(cartItem.item.price.toFixed(2)) * cartItem.quantity,
                            cartItem.item.priceUnit
                          )
                        ) +
                          ' ' +
                          t('total')}
                    </Typography>
                  </div>
                  <RemoveFromCartIcon onClick={() => removeHandler && removeHandler(cartItem)}>
                    <IcoTrash width="30" />
                  </RemoveFromCartIcon>
                </PriceItem>
              </PriceText>
            </div>
            {viewType === CartListType.VIEW_TYPE_DEFAULT && <div className="grid-container"> </div>}
          </CartItemRow>
        </Container>
      ) : (
        <CartItemRow viewType={viewType} key={cartItem.item.id}>
          {viewType === CartListType.VIEW_TYPE_DEFAULT &&
          selectionType === CartListType.SELECTION_MULTI_SELECT &&
          !isFinalEstimationPage ? (
            <SelectControl width="3rem">
              <Checkbox
                key={`checkbox-${cartItem.item.sku}`}
                checked={cartItem.selected as boolean}
                onChange={() => {
                  selectionHandler && selectionHandler(cartItem);
                }}
                width={18}
                height={18}
              />
            </SelectControl>
          ) : (
            viewType === CartListType.VIEW_TYPE_MORE &&
            selectionType === CartListType.SELECTION_REMOVE && <></>
          )}
          <CartItemText defaultValue={viewType}>
            {/* where do we get this text from? */}
            <>
              <Typography variant="body2">
                {getProductDescription(cartItem.item, languageId)}
              </Typography>
              {/* {viewType === CartListType.VIEW_TYPE_DEFAULT && (
                <Typography size={'.7rem'} weight="semi-bold">
                  {t('manufacturer_not_available')}
                </Typography>
              )} */}
            </>
          </CartItemText>
          {(viewType === CartListType.VIEW_TYPE_DEFAULT ||
            viewType === CartListType.VIEW_TYPE_MORE) && (
            <CartItemSku>
              <Typography mb={0}>{cartItem.item.stockCode || cartItem.item.sku}</Typography>
            </CartItemSku>
          )}
          {(viewType === CartListType.VIEW_TYPE_DEFAULT ||
            viewType === CartListType.VIEW_TYPE_MORE) && (
            <CartStock>
              <Typography mb={0}>{getStockText(cartItem.item, t)}</Typography>
            </CartStock>
          )}
          {viewType === CartListType.VIEW_TYPE_DEFAULT && (
            <AvgLeadTime>
              <Typography mb={0}>
                {getPartialLeadTimeText(String(cartItem.item.leadTime), t)}
              </Typography>
            </AvgLeadTime>
          )}
          <QuantityContainer defaultValue={viewType}>
            <Input
              type="number"
              id="qty"
              min={1}
              value={cartItem.quantity}
              onChange={changeHandler}
            />
          </QuantityContainer>
          <PriceText defaultValue={viewType}>
            <Typography>
              <CustomerPrice
                product={cartItem.item}
                fontSizeInREM={0.95}
                inline={true}
                handleClose={handleClose}
              />
            </Typography>
            <Typography className="total-price" size={'1rem'} weight="semi-bold">
              {typeof cartItem.item.price !== 'undefined' &&
                cartItem.item.price > 0 &&
                cartItem.item.isPurchasable &&
                String(
                  currency(
                    cartItem.item.custPrice
                      ? parseFloat(cartItem.item.custPrice.toFixed(2)) * cartItem.quantity
                      : parseFloat(cartItem.item.price.toFixed(2)) * cartItem.quantity,
                    cartItem.item.priceUnit
                  )
                ) +
                  ' ' +
                  t('total')}
            </Typography>
          </PriceText>
          {viewType === CartListType.VIEW_TYPE_DEFAULT && handleProCareAllowance && (
            <ProCareAllowance disabled={!cartItem.item.procareAllowance}>
              <Checkbox
                key={`checkbox-procare-${cartItem.item.sku}`}
                checked={cartItem.procareAllowance as boolean}
                onChange={() => {
                  handleProCareAllowance && handleProCareAllowance(cartItem);
                }}
                width={18}
                height={18}
                disabled={!cartItem.item.procareAllowance}
              />
              Yes
            </ProCareAllowance>
          )}
          {viewType === CartListType.VIEW_TYPE_DEFAULT && (
            <>
              <CartUrgentText>
                <CheckboxContainer>
                  {isFinalEstimationPage ? (
                    <Typography mb={0}>{cartItem.urgent ? 'Yes' : 'No'}</Typography>
                  ) : (
                    <Checkbox
                      key={`checkbox-${cartItem.item.sku}`}
                      checked={cartItem.urgent as boolean}
                      onChange={() => {
                        urgentChangeHandler && urgentChangeHandler(cartItem);
                      }}
                      width={18}
                      height={18}
                      margin="0"
                    />
                  )}
                </CheckboxContainer>
              </CartUrgentText>
              {isFinalEstimationPage && !enableProsealTwoWaySync && requestedDate && (
                <TentativeDate>
                  <Typography mb={0}>{formattedDate}</Typography>
                </TentativeDate>
              )}
              {isFinalEstimationPage && enableProsealTwoWaySync && (
                <TentativeDate defaultValue={viewType}>
                  <Typography mb={0}>{'N/A'}</Typography>
                </TentativeDate>
              )}
              <RemoveFromCartIcon onClick={() => removeHandler && removeHandler(cartItem)}>
                <IcoTrash width="30" />
              </RemoveFromCartIcon>
            </>
          )}
        </CartItemRow>
      )}
    </>
  );
};

export default CartListRowComponent;
